import { useEffect, useState } from 'react';
import { XPay as XPayDevelopment } from "@xstak/xpay-element-stage-v2";
import { XPay as XPayProduction } from "@xstak/xpay-element-live-v2";
import { WidgetPaymentForm } from "./WidgetPaymentForm";
import { Box, CircularProgress, Typography } from '@mui/material';
import Logo from "src/components/logo";
import axios from 'axios';

export default function XPayPaymentWidget() {
  const urlParams = new URLSearchParams(window.location.search);
  const tempPaymentId = urlParams.get('id');
  const timerParam = parseInt(urlParams.get('timer'), 10);
  const pathParam = urlParams.get('path');
  const decodedPath = decodeURIComponent(pathParam || '');

  const [isXpayReady, setIsXpayReady] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(isNaN(timerParam) ? 300 : timerParam);

  const XPay =
    process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === "staging"
      ? XPayProduction
      : XPayDevelopment;

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60).toString().padStart(2, '0');
    const sec = (seconds % 60).toString().padStart(2, '0');
    return `${min}:${sec}`;
  };

  useEffect(() => {
    if (timeLeft <= 0) return;
    const intervalId = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  useEffect(() => {
    if (window.__isXpayLoaded) {
      setIsXpayReady(false);
      return;
    }
    const checkReady = () => {
      if (window.__isXpayLoaded) {
        setIsXpayReady(false);
      } else {
        setTimeout(checkReady, 100);
      }
    };
    checkReady();
  }, []);

  useEffect(() => {
    if (!tempPaymentId) return;

    axios.get(`${process.env.REACT_APP_BACKEND_URL}/tempPaymentForWidget/${tempPaymentId}`)
      .then(response => {
        const payment = response.data;

        const combinedData = {
          customer: {
            firstName: payment?.customer?.firstName || '',
            lastName: payment?.customer?.lastName || '',
            email: payment?.customer?.email || '',
            phone: payment?.customer?.phone || '',
          },
          dataForCreateIntent: {
            amount: payment?.amount,
            partySize: payment?.partySize,
            date: payment?.date,
            venueId: payment?.venueId,
            levelId: payment?.levelId,
            endTime: payment?.endTime,
            startTime: payment?.startTime,
            tableId: payment?.tableId,
            areaId: payment?.areaId,
            slotUniqueId: payment?.slotUniqueId,
            specialRequest: payment?.specialRequest,
            occasion: payment?.occasion,
            currency: "PKR"
          },
        };

        setPaymentData(combinedData);
      })
      .catch(error => {
        if (error?.response?.status === 400) {
          window.location.href = decodedPath;
        }
      });
  }, [tempPaymentId]);

  return (
    <Box
      sx={{
        backgroundColor: '#F59B34',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >

      <Box
        sx={{
          display: 'flex',
          borderRadius: '0.3rem',
          backgroundColor: 'rgb(238, 248, 251)',
          color: 'rgb(45, 51, 63)',
          p: 1.5
        }}
      >

        {timeLeft === 0 ? (
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: 'error.main',
              fontWeight: 600,
            }}
          >
            ❌ Your reservation session has expired. Kindly start again to book a table.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              borderRadius: '0.3rem',
              backgroundColor: 'rgb(238, 248, 251)',
              color: 'rgb(45, 51, 63)',
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '14px' }}>
              Due to limited availability, we can hold this table for you for{' '}
              <strong>
                <span id="timer" style={{ fontWeight: 600 }}>
                  {formatTime(timeLeft)}
                </span>{' '}
                minutes
              </strong>
            </Typography>
          </Box>
        )}



      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          width: {
            xs: '90%',
            sm: '70%',
            md: '50%',
            lg: '40%',
          },
        }}
      >
        {!tempPaymentId ? (
          <Typography variant="h6" color="error" textAlign="center">
            ❌ You cannot access this page directly.
          </Typography>
        ) : !isXpayReady ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo />
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                color: 'error.main',
                mb: 2,
                fontWeight: 'bold',
                fontSize: '0.95rem',
              }}
            >
              ⚠️ Don’t refresh or close this tab — you’ll lose the slot booking.
            </Box>

            <Box
              sx={{
                display: 'flex',
                borderRadius: '0.3rem',
                backgroundColor: 'rgb(238, 248, 251)',
                color: 'rgb(45, 51, 63)',
                p: 1.5
              }}
            >

              {timeLeft === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'error.main',
                    fontWeight: 600,
                  }}
                >
                  Your reservation session has expired. Kindly start again to book a table.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    borderRadius: '0.3rem',
                    backgroundColor: 'rgb(238, 248, 251)',
                    color: 'rgb(45, 51, 63)',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    We’ll keep this table reserved for the next 
                    <strong>
                      <span id="timer" style={{ fontWeight: 600 }}>
                        {formatTime(timeLeft)}
                      </span>{' '}
                      minutes
                    </strong>
                  </Typography>
                </Box>
              )}



            </Box>

            <XPay
              xpay={{
                publishableKey: process.env.REACT_APP_XPAY_PUBLISHABLE_KEY,
                accountId: process.env.REACT_APP_XPAY_ACCOUNT_ID,
                hmacSecret: process.env.REACT_APP_XPAY_HMAC_SECRET,
              }}
            >
              <WidgetPaymentForm
                paymentData={paymentData}
                timeLeft={timeLeft}
                decodedPath={decodedPath}
              />
            </XPay>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
}
